import Link from '@/components/UI/Link';
import type { AgentInputV1 } from '@/model/Agent.model';
import { web } from '@/support/route';
import { type FC, type ReactNode } from 'react';
import { RiExternalLinkLine } from 'react-icons/ri';
import styles from './AgentSessionInputs.module.scss';

interface AgentSessionInputsProps {
  inputs: Array<AgentInputV1>;
}

/**
 * Renders an agent session's projected input primitives, one labelled row each. Each row's heading
 * comes from {@link heading} and its body from {@link renderValue}, both switching exhaustively on
 * `type` (no default — a new primitive is a compile error until handled). A specialised arm like
 * `study` has a heading fixed by its type, mapped here; the generic `text` arm carries a
 * server-owned `label` because its meaning varies per instance.
 */
const AgentSessionInputs: FC<AgentSessionInputsProps> = ({ inputs }) => (
  <dl className={styles.list} data-test-component="AgentSessionInputs">
    {inputs.map((input) => (
      <div key={`${input.type}-${heading(input)}-${inputValue(input)}`} className={styles.row}>
        <dt className={styles.label}>{heading(input)}</dt>
        <dd className={styles.value}>{renderValue(input)}</dd>
      </div>
    ))}
  </dl>
);

/** Row heading per primitive type: UI-mapped for specialised arms, server `label` for generic ones. */
const heading = (input: AgentInputV1): string => {
  switch (input.type) {
    case 'study':
      return 'Selected Study';
    case 'text':
      return input.label;
  }
};

/** Stable per-row key component: each arm's identifying value. Exhaustive over the union. */
const inputValue = (input: AgentInputV1): string | number => {
  switch (input.type) {
    case 'study':
      return input.study_id;
    case 'text':
      return input.value;
  }
};

/** Body renderer per primitive type. Exhaustive over the union — no default arm. */
const renderValue = (input: AgentInputV1): ReactNode => {
  switch (input.type) {
    case 'study':
      return (
        <Link
          className={styles.link}
          to={web('studies.show', { id: input.study_id })}
          openTab
          data-test-element="study-link"
        >
          <span>{input.study_name}</span>
          <RiExternalLinkLine className={styles.linkIcon} aria-hidden />
        </Link>
      );
    case 'text':
      return input.value;
  }
};

export default AgentSessionInputs;
